import { formatDate } from "@elektr/shared";

import { compact, layoutChart } from "@/lib/chart";

export interface TrendPoint {
  /** ISO sana */
  date: string;
  value: number;
}
export interface ForecastPoint extends TrendPoint {
  low: number;
  high: number;
}

const BOX = { width: 640, height: 240, left: 56, right: 12, top: 12, bottom: 28 };
const path = (pts: [number, number][]) =>
  pts.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");

/** Yengil SVG chiziqli grafik: haqiqiy qiymatlar + (ixtiyoriy) prognoz va uning ishonch oralig'i. */
export function TrendChart({
  history,
  forecast = [],
  label,
  format = (n) => String(n),
}: {
  history: TrendPoint[];
  forecast?: ForecastPoint[];
  label: string;
  format?: (n: number) => string;
}) {
  const l = layoutChart({ history: history.map((p) => p.value), forecast }, BOX);
  const all = [...history, ...forecast];
  const xs = [...l.history, ...l.forecast.slice(history.length ? 1 : 0)];
  const labelAt = new Set([0, Math.floor((all.length - 1) / 2), all.length - 1]);
  return (
    <svg
      viewBox={`0 0 ${BOX.width} ${BOX.height}`}
      className="h-auto w-full"
      role="img"
      aria-label={label}
    >
      {l.ticks.map((t) => (
        <g key={t.value}>
          <line
            x1={BOX.left}
            x2={BOX.width - BOX.right}
            y1={t.y}
            y2={t.y}
            className="stroke-border"
            strokeWidth={1}
          />
          <text
            x={BOX.left - 6}
            y={t.y + 4}
            textAnchor="end"
            className="fill-muted-foreground text-[11px]"
          >
            {compact(t.value)}
          </text>
        </g>
      ))}
      {l.band.length > 0 && (
        <path d={`${path(l.band)} Z`} className="fill-primary/15" data-testid="band" />
      )}
      {l.history.length > 1 && (
        <path
          d={path(l.history)}
          fill="none"
          className="stroke-primary"
          strokeWidth={2.5}
          strokeLinejoin="round"
        />
      )}
      {l.forecast.length > 1 && (
        <path
          d={path(l.forecast)}
          fill="none"
          className="stroke-primary"
          strokeWidth={2.5}
          strokeDasharray="6 5"
          data-testid="forecast-line"
        />
      )}
      {all.map((p, i) => {
        const [x, y] = xs[i] ?? [0, 0];
        const isForecast = i >= history.length;
        return (
          <g key={`${p.date}-${i}`}>
            <circle
              cx={x}
              cy={y}
              r={history.length + forecast.length > 40 ? 0 : 3.5}
              className={isForecast ? "fill-background stroke-primary" : "fill-primary"}
              strokeWidth={2}
            >
              <title>{`${formatDate(p.date)}: ${format(p.value)}`}</title>
            </circle>
            {labelAt.has(i) && (
              <text
                x={x}
                y={BOX.height - 8}
                textAnchor={i === 0 ? "start" : i === all.length - 1 ? "end" : "middle"}
                className="fill-muted-foreground text-[11px]"
              >
                {formatDate(p.date).slice(0, 5)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}
